import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { getCroppedImg } from "@/lib/cropImage";

type Props = {
  imageSrc: string;
  aspect: number;
  outputWidth: number;
  outputHeight: number;
  title?: string;
  onCancel: () => void;
  onConfirm: (file: File) => void;
};

export default function ImageCropModal({
  imageSrc,
  aspect,
  outputWidth,
  outputHeight,
  title = "Ajustar imagem",
  onCancel,
  onConfirm,
}: Props) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  async function handleConfirm() {
    if (!croppedAreaPixels) return;

    try {
      setSaving(true);

      const blob = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        outputWidth,
        outputHeight
      );

      const file = new File([blob], "imagem-cortada.jpg", {
        type: "image/jpeg",
      });

      onConfirm(file);
    } catch (error) {
      console.error("Erro ao recortar imagem:", error);
      alert("Não foi possível ajustar a imagem.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-bold text-lg">{title}</h2>
          <button
            type="button"
            className="px-3 py-1 rounded-lg border text-sm font-semibold"
            onClick={onCancel}
          >
            Fechar
          </button>
        </div>

        <div className="p-4">
          <div className="relative w-full h-[420px] bg-black rounded-xl overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid={true}
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold mb-2">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-xl border font-semibold"
              onClick={onCancel}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="px-4 py-2 rounded-xl font-semibold text-white disabled:opacity-50"
              onClick={handleConfirm}
              disabled={saving}
              style={{ backgroundColor: "#FE5668" }}
            >
              {saving ? "Salvando..." : "Usar esta imagem"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}